<script setup lang="ts">
import { getTenancyStatus, getTenancyAddress, getExternalIdForTenancy, getTenancyDaysOnMarket } from "~/helpers/entities/tenancy-helpers";
import { SALES_STATUS, getValueForSegmentedBar, getSalesStatusFromIndex, getSalesStatusText } from "~/helpers/modules/sales-helpers";
import { AssetManagementTenancyStatus, TenancyHeaderDocument, UpdateTenancySalesStatusDocument } from "~/graphql/generated/graphql";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { getTenantStatus } from "~/helpers/entities/tenant-helpers";
import moment from "moment";

const route = useRoute();

const t = useTranslator();
const filter = useFilter();

const tenancyId = computed(() => route.params.tenancyId as string);

const headerQuery = useQuery(TenancyHeaderDocument, { id: tenancyId.value });

const tenancy = computed(() => headerQuery.result.value?.assetManagementTenancy);

const loading = computed(() => headerQuery.loading.value);

const address = computed(() => (tenancy.value ? getTenancyAddress(tenancy.value) : undefined));
const externalId = computed(() => (tenancy.value ? getExternalIdForTenancy(tenancy.value) : undefined));
const salesStatus = computed(() => tenancy.value?.status);
const salesStatusInProgress = computed(() => !(["REMOVED", "NOT_FOR_SALE", "UNKNOWN"] as Nullable<AssetManagementTenancyStatus>[]).includes(salesStatus.value));
const daysOnMarket = computed(() => (tenancy.value ? getTenancyDaysOnMarket(tenancy.value) : undefined));
const salesStatusses = computed(() => {
  return Object.keys(SALES_STATUS).map((key, index) => {
    return {
      value: getValueForSegmentedBar(key as AssetManagementTenancyStatus),
      key: key as AssetManagementTenancyStatus,
    };
  });
});

const inactive = computed(() => (tenancy.value ? moment.utc(tenancy.value.endDate).isBefore(moment.utc()) : undefined));

const tenantStatusOptions = useTenantStatusOptions();
const tenancyStatusOptions = useTenancyStatusOptions();

const tenantStatusOption = computed(() => (tenancy.value ? tenantStatusOptions.value[getTenantStatus(tenancy.value)] : undefined));
const tenancyStatusOption = computed(() => (tenancy.value ? tenancyStatusOptions.value[getTenancyStatus(tenancy.value)] : undefined));

const updateSalesStatusMutation = useMutation(UpdateTenancySalesStatusDocument);

const updateSalesStatus = (status: number) => {
  updateSalesStatusMutation.mutate({ input: { id: tenancyId.value, status: getSalesStatusFromIndex(status) as AssetManagementTenancyStatus } });
};

const startSaleProcess = () => {
  updateSalesStatusMutation.mutate({ input: { id: tenancyId.value, status: AssetManagementTenancyStatus.PreparingForSale } });
};

const tabs = useUrlTabsTenancy();

usePageTitle({ position: 1, title: () => tenancy.value?.name });

watch(
  () => tenancy.value,
  () => {
    if (tenancy.value?.name) {
      useBreadcrumbs().setDynamicLabel("tenancyId", tenancy.value.name);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :class="$style.container">
    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPadding>
          <template #container>
            <Header v-if="!loading && !!tenancy" :tenancy="tenancy" :colorBackground="'--color-accent-100'" :colorForeground="'--color-primary'" />
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>

    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPaddingTop>
          <template #container>
            <WidgetLayout outline>
              <template #widgetRows>
                <WidgetLayoutRowCellSingle>
                  <template #widget>
                    <WidgetBlank omitPaddingContent omitPaddingTop omitClickMore>
                      <template #container>
                        <EntityCard
                          v-if="!loading && tenancy"
                          trackingOrigin="portfolio-tenancy-card"
                          :colorBackground="'--color-white'"
                          :colorForeground="'--color-primary'"
                          :colorSeperators="'--color-accent-200'"
                          :alignControlsEnd="!salesStatusInProgress ? true : false"
                        >
                          <template #header>
                            <KeyValuePair direction="horizontal">
                              <template #containerStart>
                                <Icon id="icon-condo-tt" size="big" color="--color-primary" />
                              </template>

                              <template #containerEnd>
                                <Copy type="header" size="large" color="--color-primary"> {{ address }} </Copy>
                              </template>
                            </KeyValuePair>
                          </template>

                          <template #byline>
                            <div style="display: flex; flex-direction: row; padding: 0.5rem 0rem; gap: 1rem; align-items: center">
                              <KeyValuePair direction="horizontal" renderAsLabel labelColorNamespace="accent">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary"> {{ t("PORTFOLIO_TYPE") }} </Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="body" size="small" color="--color-primary"> {{ tenancy.tenancyType }} </Copy>
                                </template>
                              </KeyValuePair>

                              <KeyValuePair direction="horizontal" renderAsLabel labelColorNamespace="accent">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary">{{ t("PORTFOLIO_EXTERNAL_ID") }}</Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="number" size="small" color="--color-primary" wrap>
                                    {{ externalId }}
                                  </Copy>
                                </template>
                              </KeyValuePair>

                              <template v-if="tenancy">
                                <template v-if="!inactive">
                                  <KeyValuePair
                                    v-if="tenancyStatusOption"
                                    direction="horizontal"
                                    renderAsLabel
                                    :labelColorNamespace="tenancyStatusOption.color"
                                  >
                                    <template #containerStart>
                                      <Copy type="label" size="small" :color="`--color-${tenancyStatusOption.color}-300`">{{
                                        filter.capitalize(t("PORTFOLIO_VIEW_TENANCIES_TENANCY_STATUS"))
                                      }}</Copy>
                                    </template>

                                    <template #containerEnd>
                                      <Copy type="body" size="small" :color="`--color-${tenancyStatusOption.color}-300`">{{ tenancyStatusOption.name }} </Copy>
                                    </template>
                                  </KeyValuePair>

                                  <KeyValuePair v-if="tenantStatusOption" direction="horizontal" renderAsLabel :labelColorNamespace="tenantStatusOption.color">
                                    <template #containerStart>
                                      <Copy type="label" size="small" :color="`--color-${tenantStatusOption.color}-300`">{{
                                        filter.capitalize(t("PORTFOLIO_VIEW_TENANCIES_TENANT_STATUS"))
                                      }}</Copy>
                                    </template>

                                    <template #containerEnd>
                                      <Copy type="body" size="small" :color="`--color-${tenantStatusOption.color}-300`">{{ tenantStatusOption.name }} </Copy>
                                    </template>
                                  </KeyValuePair>
                                </template>
                                <template v-else>
                                  <KeyValuePair v-if="tenantStatusOption" direction="horizontal" renderAsLabel labelColorNamespace="yellow">
                                    <template #containerStart>
                                      <Copy type="label" size="small" :color="`--color-yellow-300`">{{
                                        filter.capitalize(t("PORTFOLIO_VIEW_TENANCIES_TENANCY_STATUS"))
                                      }}</Copy>
                                    </template>

                                    <template #containerEnd>
                                      <Copy type="body" size="small" :color="`--color-yellow-300`"> {{ t("PORTFOLIO_TENANCY_INACTIVE") }} </Copy>
                                    </template>
                                  </KeyValuePair>
                                </template>
                              </template>
                            </div>
                          </template>

                          <template #controlsEnd>
                            <div v-if="salesStatusInProgress" style="display: flex">
                              <KeyValuePair direction="vertical">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-white"> {{ t("PORTFOLIO_SALES_STATUS") }} </Copy>
                                </template>

                                <template #containerEnd>
                                  <SegmentedDropDown
                                    :modelValue="getValueForSegmentedBar(salesStatus)"
                                    :label="salesStatus ? getSalesStatusText(salesStatus) : t('PORTFOLIO_SELECT_STATUS')"
                                    colorName="green"
                                    minWidth="12rem"
                                    maxWidth="12rem"
                                    :valueIsLessThanIndex="true"
                                    @update:modelValue="updateSalesStatus"
                                  >
                                    <DropDownItem
                                      v-for="status in salesStatusses"
                                      :key="status.value"
                                      :label="getSalesStatusText(status.key)"
                                      :value="status.value"
                                    />
                                  </SegmentedDropDown>
                                </template>
                              </KeyValuePair>

                              <CompositionSpacer direction="horizontal" size="medium" />

                              <KeyValuePair direction="vertical">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary"> {{ t("PORTFOLIO_DAYS_ON_MARKET") }} </Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="number" size="huge" color="--color-primary"> {{ daysOnMarket }} </Copy>
                                </template>
                              </KeyValuePair>
                            </div>

                            <div v-else>
                              <PushButton size="medium" color="--color-transparent" underline :disabled="loading" @click="startSaleProcess">
                                <template #iconBefore>
                                  <Icon id="add" size="medium" color="--color-primary" />
                                </template>

                                <template #copy>
                                  <Copy type="bodyStrong" size="small" color="--color-primary">{{ t("PORTFOLIO_PROPERTY_START_SALES_STATUS") }}</Copy>
                                </template>
                              </PushButton>
                            </div>
                          </template>
                        </EntityCard>
                      </template>
                    </WidgetBlank></template
                  ></WidgetLayoutRowCellSingle
                >

                <WidgetLayoutRowCellSingle>
                  <template #widget>
                    <WidgetBlank omitPaddingContent omitPaddingTop omitClickMore>
                      <template #container>
                        <UrlTabContainer :items="tabs" basePath="portfolio-tenancy-tenancyId" context="tenancy" :params="{ tenancyId }" />
                      </template>
                    </WidgetBlank>
                  </template>
                </WidgetLayoutRowCellSingle>
              </template>
            </WidgetLayout>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>

    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPaddingTop>
          <template #container>
            <WidgetLayout outline>
              <template #widgetRows>
                <slot />
              </template>
            </WidgetLayout>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>
  </div>
</template>

<style module>
.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chip {
  display: flex;
  background-color: var(--color-white);
  border-radius: var(--border-radius-medium);
  padding: 0.25rem;
  justify-content: center;
  align-items: center;
}

.dot {
  display: flex;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background-color: var(--color-red-500);
}

.dot.active {
  background-color: var(--color-green-700);
}

.icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.label {
  display: flex;
  margin-left: 0.25rem;
}

:global(.table-naked) {
  border-collapse: collapse;
  width: 100%;
}

:global(.table-naked td) {
  padding: 0.25rem 1rem;
}
</style>
